export const ERROR_KIND = Object.freeze({
  AUTH: 'auth',
  HTTP: 'http',
  NETWORK: 'network',
  SERVER: 'server',
  TIMEOUT: 'timeout',
  UNKNOWN: 'unknown',
});

export const CONNECTION_STATE = Object.freeze({
  API_UNREACHABLE: 'api_unreachable',
  HEALTHY: 'healthy',
  OFFLINE: 'offline',
  UNKNOWN: 'unknown',
});

const NETWORK_ERROR_MARKERS = [
  'failed to fetch',
  'load failed',
  'networkerror',
  'network error',
  '[network_fail]',
];

export function classifyRequestError(error) {
  const message = String(error?.message || error || '');
  const normalizedMessage = message.toLowerCase();

  if (error?.name === 'AbortError' || normalizedMessage.includes('[api_timeout]')) {
    return ERROR_KIND.TIMEOUT;
  }

  if (
    normalizedMessage.includes('[auth_')
    || normalizedMessage.includes('[api_http_401]')
    || normalizedMessage.includes('[api_http_403]')
  ) {
    return ERROR_KIND.AUTH;
  }

  const httpStatusMatch = normalizedMessage.match(/\[api_http_(\d{3})]/);
  if (httpStatusMatch) {
    const status = Number(httpStatusMatch[1]);
    return status >= 500 ? ERROR_KIND.SERVER : ERROR_KIND.HTTP;
  }

  if (
    error instanceof TypeError
    || NETWORK_ERROR_MARKERS.some((marker) => normalizedMessage.includes(marker))
  ) {
    return ERROR_KIND.NETWORK;
  }

  return ERROR_KIND.UNKNOWN;
}

const appendProbeParam = (url) => {
  const separator = String(url).includes('?') ? '&' : '?';
  return `${url}${separator}_wq_probe=${Date.now()}`;
};

async function probeUrl(url, { fetchImpl, timeoutMs }) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const result = await fetchImpl(appendProbeParam(url), {
      cache: 'no-store',
      credentials: 'omit',
      method: 'GET',
      redirect: 'follow',
      signal: controller.signal,
    });

    // 차단 여부 판정에서는 HTTP status보다 Response 도착 여부가 중요하다.
    return {
      errorKind: null,
      httpStatus: Number.isFinite(result?.status) ? result.status : null,
      reachability: 'reachable',
    };
  } catch (error) {
    const errorKind = classifyRequestError(error);
    return {
      errorKind,
      httpStatus: null,
      reachability: errorKind === ERROR_KIND.TIMEOUT ? 'timeout' : 'unreachable',
    };
  } finally {
    clearTimeout(timeoutId);
  }
}

export async function diagnoseApiReachability({
  apiBaseUrl,
  fetchImpl = fetch,
  sameOriginUrl = '/manifest.json',
  timeoutMs = 2500,
} = {}) {
  if (!apiBaseUrl || typeof fetchImpl !== 'function') {
    return {
      evidence: {
        api: 'unknown',
        apiErrorKind: ERROR_KIND.UNKNOWN,
        apiStatus: null,
        sameOrigin: 'unknown',
        sameOriginErrorKind: ERROR_KIND.UNKNOWN,
        sameOriginStatus: null,
      },
      state: CONNECTION_STATE.UNKNOWN,
    };
  }

  const normalizedApiBaseUrl = String(apiBaseUrl).replace(/\/+$/, '');
  const [sameOriginProbe, apiProbe] = await Promise.all([
    probeUrl(sameOriginUrl, { fetchImpl, timeoutMs }),
    probeUrl(`${normalizedApiBaseUrl}/healthz`, { fetchImpl, timeoutMs }),
  ]);

  let state = CONNECTION_STATE.UNKNOWN;
  if (apiProbe.reachability === 'reachable') {
    state = CONNECTION_STATE.HEALTHY;
  } else if (sameOriginProbe.reachability === 'reachable') {
    state = CONNECTION_STATE.API_UNREACHABLE;
  } else if (
    sameOriginProbe.reachability !== 'reachable'
    && apiProbe.reachability !== 'reachable'
  ) {
    state = CONNECTION_STATE.OFFLINE;
  }

  return {
    evidence: {
      api: apiProbe.reachability,
      apiErrorKind: apiProbe.errorKind,
      apiStatus: apiProbe.httpStatus,
      sameOrigin: sameOriginProbe.reachability,
      sameOriginErrorKind: sameOriginProbe.errorKind,
      sameOriginStatus: sameOriginProbe.httpStatus,
    },
    state,
  };
}
